<script setup lang="ts">
import { Order } from '@commercetools/platform-sdk';
import { cents } from '@nuts/auto-delivery-sdk/dist/utils/money';
import {
  DataCollector,
  FetchPaymentMethodsPayload,
  HostedFieldsTokenizePayload,
  PayPalTokenizePayload,
} from 'braintree-web';
import { storeToRefs } from 'pinia';
import { computed, ComputedRef, inject, onBeforeMount, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import { createContact } from '@/api/contacts';
import { PlaceOrderRequest } from '@/api/placeOrder';
import CreditCardLogo from '@/components/base/assets/CreditCardLogo.vue';
import FormControlsWrapper from '@/components/base/form/FormControlsWrapper.vue';
import FormInput from '@/components/base/form/FormInput.vue';
import RadioGroup from '@/components/base/form/RadioGroup.vue';
import RadioGroupItem from '@/components/base/form/RadioGroupItem.vue';
import InformationBanner from '@/components/base/layout/InformationBanner.vue';
import Notification from '@/components/base/Notification.vue';
import TextButton from '@/components/base/TextButton.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import WindowObserver from '@/components/base/WindowObserver.vue';
import BillingAddress from '@/components/checkout/cxMode/BillingAddress.vue';
import CxCreateAccount from '@/components/checkout/cxMode/CreateAccount.vue';
import CxNewsletterSignup from '@/components/checkout/cxMode/NewsletterSignup.vue';
import OrderTags from '@/components/checkout/cxMode/OrderTags.vue';
import PriceAdjustment from '@/components/checkout/cxMode/PriceAdjustment.vue';
import BraintreePaypalButton from '@/components/checkout/steps/payment/BraintreePaypalButton.vue';
import CheckoutApplePayButton from '@/components/checkout/steps/payment/CheckoutApplePayButton.vue';
import ConfirmCvv from '@/components/checkout/steps/payment/ConfirmCvv.vue';
import CreateAccount from '@/components/checkout/steps/payment/CreateAccount.vue';
import CreditCardForm from '@/components/checkout/steps/payment/CreditCardForm.vue';
import DiscountCodeEntry from '@/components/checkout/steps/payment/DiscountCodeEntry.vue';
import GiftCertificateOption from '@/components/checkout/steps/payment/GiftCertificateOption.vue';
import SavedCreditCards from '@/components/checkout/steps/payment/SavedCreditCards.vue';
import ThirdPartyPaymentEmailDisplay from '@/components/checkout/steps/payment/ThirdPartyPaymentEmailDisplay.vue';
import { useAuth } from '@/composables/useAuth';
import { useCart } from '@/composables/useCart';
import { useCheckout } from '@/composables/useCheckout';
import { useDelivery } from '@/composables/useDelivery';
import { AvailablePaymentMethod, usePayment } from '@/composables/usePayment';
import { hasNonExpiredDefaultPaymentMethod } from '@/lib/checkout/payment';
import { BraintreeStore } from '@/stores/braintree';
import { useCustomer } from '@/stores/customer';
import { useSession } from '@/stores/session';
import { NutsAddress } from '@/utils/address';
import { CheckoutEvents } from '@/utils/analytics';
import { generateRecaptcha } from '@/utils/captcha';
import { hasDigital } from '@/utils/cart';
import { NutsRedirect } from '@/utils/exceptions';
import { isDefined } from '@/utils/isDefined';
import { reportError } from '@/utils/reportError';

export interface TokenizedCreditCard extends HostedFieldsTokenizePayload {
  billingAddress: NutsAddress;
  saveToWallet?: boolean;
}

interface SavedPaymentMethod extends FetchPaymentMethodsPayload {
  billingAddress?: NutsAddress;
  /** Customer email, defined when `type` is `ApplePay` or `PayPal` */
  external_account_token?: string;
  id: number;
}

export interface Offline {
  billingAddress: NutsAddress;
}

export interface PaymentStepProps {
  availablePaymentMethods?: ComputedRef<AvailablePaymentMethod[]>;
  disablePlaceOrderButton?: boolean;
  paymentMethods?: SavedPaymentMethod[];
}

function isUsBankAccount(payment: SavedPaymentMethod) {
  if (!(payment.details && 'cardType' in payment.details)) return false;
  return payment.details.cardType === 'US Bank Account';
}

function isSavedPayment<T extends Object>(
  payment?: SavedPaymentMethod | T,
): payment is SavedPaymentMethod {
  return isDefined(payment) && 'id' in payment;
}

function isTokenizedPayment<T extends Object>(payment?: SavedPaymentMethod | T): payment is T {
  return isDefined(payment) && !('id' in payment);
}

const props = defineProps<PaymentStepProps>();

const emit = defineEmits<{ 'order-placed': [receiptToken: string, order: Order] }>();

const isAdmin = inject('isAdmin', false);
const router = useRouter();
const store = useStore();
const { permissions, sessionEmail } = useAuth(store);
const {
  cartCustomFields,
  containsAutoDelivery,
  customerEmail,
  giftCertificate,
  lineItems,
  setGiftCertificate,
  unassignedLineItems,
} = useCart(store);
const { clearCheckout, cxMode, isB2B, placeOrder } = useCheckout(store);
const { customer, hasBusinessAccount, offerNewsletter } = storeToRefs(useCustomer());
const { standardShipments } = useDelivery(store);
const { estimatedAmountToCharge, estimatedGiftCertificateCharge, paymentItems } = usePayment(
  store,
  cxMode,
);
const { isSignedLoginTypeSocialLogin, signedLoginType } = storeToRefs(useSession());

const giftCertificatePaymentMethod = computed(() =>
  props.availablePaymentMethods?.value.find((m) => m.type === 'GiftCertificate'),
);
const otherPaymentMethods = computed(() =>
  props.availablePaymentMethods?.value
    .filter((m) => m.type !== 'GiftCertificate')
    .map((m) => ({ ...m, type: m.type.replaceAll(' ', '') })),
);
const braintreeDataCollector = ref<DataCollector>();
const braintreeToken = ref('');
const createAccountPassword = ref<string>();
const cxCreateAccount = ref(false);
const newsletterSignup = ref(false);
const orderTags = ref<string[]>([]);
const useGiftCertificate = ref(!!estimatedGiftCertificateCharge.value);
const showPriceAdjustment = computed(
  () => cxMode.value && permissions.value.createOrderManualAdjustment,
);
const usePriceAdjustment = ref(false);

const paymentOptions: {
  ApplePay?: SavedPaymentMethod;
  CreditCard?: SavedPaymentMethod | TokenizedCreditCard;
  Offline?: Offline;
  PayPal?: SavedPaymentMethod | PayPalTokenizePayload;
} = reactive({
  ApplePay: undefined,
  CreditCard: undefined,
  Offline: undefined,
  PayPal: undefined,
});

const selectedPaymentOption = ref<keyof typeof paymentOptions | 'CreditAccount'>('CreditCard');

const showSavedCards = ref(false);
const paymentError = ref('');
const paymentErrorElement = ref<HTMLElement>();
const paymentErrorIntersection = ref<IntersectionObserverEntry>();

const confirmationCvv = ref<TokenizedCreditCard>();
const purchaseOrderNumber = ref();

const showPurchaseOrderNumber = computed(
  () => cxMode.value && (isB2B.value || hasBusinessAccount.value),
);
const currentCardType = computed<string | undefined>(
  () => paymentOptions.CreditCard?.details?.cardType,
);
const hasOnlyUSBankAccount = computed(
  () => !!props.paymentMethods?.length && props.paymentMethods.every(isUsBankAccount),
);
const hasValidPaymentMethod = computed(() => {
  if (selectedPaymentOption.value === 'Offline') {
    if (!orderTags.value.length) return false;
    if (!paymentOptions.Offline?.billingAddress) return false;
  }
  if (showSavedCards.value) return false;
  return (
    estimatedAmountToCharge.value.centAmount === 0 ||
    selectedPaymentOption.value === 'CreditAccount' ||
    selectedPaymentOption.value === 'Offline' ||
    !!paymentOptions[selectedPaymentOption.value]?.nonce
  );
});

const placeOrderDisabledByAutoDelivery = computed(() => {
  if (!containsAutoDelivery.value || customer.value) return false;
  if (!customerEmail.value) return true;
  return cxMode.value ? !cxCreateAccount.value : (createAccountPassword.value ?? '').length < 6;
});

const requireAuth = computed(() => {
  if (isSignedLoginTypeSocialLogin.value) return false;

  if (hasDigital(lineItems.value)) return true;

  return !standardShipments.value.every((shipment) => !!shipment.address.signedTrustedMatchkey);
});

const savedPaymentMethods = computed<SavedPaymentMethod[]>(() => {
  const paymentMethods = props.paymentMethods ?? [];

  return containsAutoDelivery.value
    ? paymentMethods.filter((p) => !isUsBankAccount(p))
    : paymentMethods;
});

const savedCreditCards = computed<SavedPaymentMethod[]>(() =>
  savedPaymentMethods.value.filter((p) => p.type !== 'PayPal' && p.type !== 'ApplePay'),
);

const isSelectedCreditCardInCustomersWallet = computed(() => {
  const creditCardOption = paymentOptions.CreditCard;
  if (!isSavedPayment(creditCardOption)) return false;
  return savedCreditCards.value.some((creditCard) => creditCard.id === creditCardOption.id);
});

const displayConfirmCVV = computed(() => {
  if (!requireAuth.value) return false;
  if (selectedPaymentOption.value !== 'CreditCard') return false;
  if (currentCardType.value === 'US Bank Account') return false;
  if (!isSelectedCreditCardInCustomersWallet.value) return false;
  if (
    useGiftCertificate.value &&
    estimatedGiftCertificateCharge.value &&
    cents(estimatedGiftCertificateCharge.value) >= cents(estimatedAmountToCharge.value)
  )
    return false;
  return true;
});

const showNewCardForm = computed(() => {
  if (selectedPaymentOption.value !== 'CreditCard') return false;
  return savedCreditCards.value.length <= 0;
});

const savedPaypal = computed(() => {
  if (requireAuth.value || !customer?.value?.custom?.fields?.braintreeId) return undefined;
  return savedPaymentMethods.value.find((p) => p.type === 'PayPal');
});

const savedApplePay = computed(() => {
  if (requireAuth.value || !customer?.value?.custom?.fields?.braintreeId) return undefined;
  return savedPaymentMethods.value.find((p) => p.type === 'ApplePay');
});

const initializePaymentMethods = async () => {
  Object.keys(paymentOptions).forEach((key) => {
    paymentOptions[key as keyof typeof paymentOptions] = undefined;
  });
  selectedPaymentOption.value = 'CreditCard';

  paymentOptions.PayPal = savedPaypal.value;
  paymentOptions.ApplePay = savedApplePay.value;

  let defaultPaymentMethod =
    savedPaymentMethods.value.find((m) => m.default) ?? savedPaymentMethods.value[0];
  if (!defaultPaymentMethod) return;

  const [savedCreditCard] = savedCreditCards.value;
  if (savedCreditCard) {
    const selected =
      containsAutoDelivery.value && isUsBankAccount(savedCreditCard)
        ? savedCreditCards.value.find((p) => !isUsBankAccount(p))
        : savedCreditCard;
    if (selected) {
      paymentOptions.CreditCard = selected;
    }
  }

  if (
    cxMode.value ||
    (defaultPaymentMethod.type === 'PayPal' && !savedPaypal.value) ||
    (defaultPaymentMethod.type === 'ApplePay' && !savedApplePay.value)
  ) {
    if (defaultPaymentMethod.type === 'PayPal' || defaultPaymentMethod.type === 'ApplePay') {
      if (savedCreditCard) {
        defaultPaymentMethod = savedCreditCard;
      } else {
        return;
      }
    }
  }

  // Us Bank Account should be considered a credit card for the purposes of the payment method selector
  selectedPaymentOption.value = defaultPaymentMethod.type.replace(
    'UsBankAccount',
    'CreditCard',
  ) as keyof typeof paymentOptions;
};

let braintreeStore: BraintreeStore;
onBeforeMount(async () => {
  initializePaymentMethods();
  const { useBraintree } = await import('@/stores/braintree');
  braintreeStore = useBraintree();
  braintreeToken.value = (await braintreeStore.loadAuthToken()) ?? '';
  braintreeDataCollector.value = await braintreeStore.initDataCollector();
});

watch(savedPaymentMethods, initializePaymentMethods);

const intersectPaymentError = (intersectionObserver: IntersectionObserverEntry) => {
  paymentErrorIntersection.value = intersectionObserver;
};

const exitPaymentError = () => {
  paymentErrorIntersection.value = undefined;
};

const submitting = ref(false);

const hasDefaultMethodNonExpired = computed(() =>
  hasNonExpiredDefaultPaymentMethod(savedPaymentMethods.value),
);

const saveCardOption = computed(
  () => !(containsAutoDelivery.value && !hasDefaultMethodNonExpired.value),
);

const onPlaceOrder = async () => {
  const captchaResponse = await generateRecaptcha();
  if (!hasValidPaymentMethod.value || submitting.value) return;
  submitting.value = true;

  if (cxMode.value && customerEmail.value) {
    createContact({
      b2b: isB2B.value,
      b2bSource: isB2B.value ? 'cx checkout buying for business' : undefined,
      email: customerEmail.value,
      subscribed: !offerNewsletter.value || newsletterSignup.value ? true : 'unchecked',
    });
  }

  const paymentNonces = [];
  if (
    selectedPaymentOption.value === 'CreditAccount' ||
    selectedPaymentOption.value === 'Offline'
  ) {
    const { nonce } =
      props.availablePaymentMethods?.value.find((pm) => pm.type === selectedPaymentOption.value) ??
      {};
    if (nonce) {
      paymentNonces.push(nonce);
    }
  } else {
    const selectedPaymentMethod = paymentOptions[selectedPaymentOption.value];

    if (selectedPaymentMethod) {
      const nonceType =
        isSavedPayment(selectedPaymentMethod) && isUsBankAccount(selectedPaymentMethod)
          ? 'us_bank_account:'
          : '';

      let nonce = `${nonceType}${selectedPaymentMethod.nonce}`;
      if (displayConfirmCVV.value && confirmationCvv.value)
        nonce += `?cvv_nonce=${confirmationCvv.value.nonce}`;

      paymentNonces.push(nonce);
    }
  }
  try {
    let billingAddress: NutsAddress | undefined;
    let saveToWallet: PlaceOrderRequest['saveToWallet'];
    let usingDifferentPaypal = false;

    if (
      selectedPaymentOption.value === 'PayPal' &&
      // PayPal should always be tokenized at this stage
      isTokenizedPayment(paymentOptions.PayPal)
    ) {
      const {
        countryCode,
        email,
        firstName,
        lastName,
        phone,
        billingAddress: paypalBillingAddress,
      } = paymentOptions.PayPal.details;

      usingDifferentPaypal =
        !!savedPaypal.value?.external_account_token &&
        savedPaypal.value.external_account_token !== email;
      saveToWallet = !savedPaypal.value;
      billingAddress = paypalBillingAddress
        ? NutsAddress.fromPayPalTokenization(paymentOptions.PayPal.details)
        : {
            country: countryCode,
            email,
            name: `${firstName} ${lastName}`,
            phone,
          };
    } else if (selectedPaymentOption.value === 'ApplePay') {
      saveToWallet = !savedApplePay.value;
    } else if (paymentOptions.CreditCard?.billingAddress) {
      billingAddress = paymentOptions.CreditCard.billingAddress;
      saveToWallet = isTokenizedPayment(paymentOptions.CreditCard)
        ? paymentOptions.CreditCard.saveToWallet
        : undefined;
    } else if (selectedPaymentOption.value === 'Offline' && paymentOptions.Offline) {
      billingAddress = paymentOptions.Offline.billingAddress;
    }

    let placeOrderRequest: Omit<PlaceOrderRequest, 'cartId' | 'version'> = {
      billingAddress,
      'g-recaptcha-response': captchaResponse,
      paymentNonces,
      signedLoginType: signedLoginType.value,
      triggerSetPasswordFlow: cxCreateAccount.value ?? false,
    };

    const hasDefaultMethod = savedPaymentMethods.value.some((m) => m.default);
    const setAsDefaultInWallet =
      !usingDifferentPaypal &&
      ((saveToWallet && !hasDefaultMethod) ||
        (containsAutoDelivery.value && !hasDefaultMethodNonExpired.value));

    if (cxMode.value) {
      const placedByEmployee = sessionEmail.value!; // must have a session to be in CX Mode
      const reshippedFromOrderNumber = cartCustomFields.value?.reshippedFromOrderNumber;
      const allOrderTags = [
        cartCustomFields.value.orderTags,
        orderTags.value,
        cartCustomFields.value.reshippedFromOrderNumber ? 'Reshipment' : undefined,
      ]
        .flat()
        .filter(isDefined);
      placeOrderRequest = {
        ...placeOrderRequest,
        orderTags: allOrderTags,
        placedByEmployee,
        purchaseOrderNumber: purchaseOrderNumber.value,
        reshippedFromOrderNumber,
        saveToWallet,
        setAsDefaultInWallet,
      };
      if (placeOrderRequest.triggerSetPasswordFlow) {
        placeOrderRequest = {
          ...placeOrderRequest,
          isB2b: isB2B.value,
        };
      }
    } else {
      placeOrderRequest = {
        ...placeOrderRequest,
        deviceData: braintreeDataCollector.value?.deviceData,
        isB2b: isB2B.value,
        newPassword: createAccountPassword.value,
        saveToWallet,
        setAsDefaultInWallet,
        triggerSetPasswordFlow: false,
      };
    }

    // 'Gift Certificate' is removed from paymentItems in placeOrder, so take a snapshot here
    // before passing to `CheckoutEvents.purchase`
    const paymentItemsSnapshot = paymentItems.value;
    const { order, receiptToken } = await placeOrder(placeOrderRequest);

    let shipmentPickupCarrier;
    let shipmentPickupCarrierCode;

    if (standardShipments.value.length === 1) {
      const { customFields } = standardShipments.value[0];
      shipmentPickupCarrierCode = customFields?.shipmentPickupCarrierCode;
      shipmentPickupCarrier = customFields?.shipmentPickupCarrier;
    }

    CheckoutEvents.purchase({
      currentCardType: currentCardType.value,
      isAdmin,
      order,
      paymentItems: paymentItemsSnapshot,
      paymentOption: selectedPaymentOption.value,
      receiptToken,
      shipmentPickupCarrier,
      shipmentPickupCarrierCode,
    });
    emit('order-placed', receiptToken, order);

    if (cxMode.value) {
      clearCheckout();
    }
  } catch (error: unknown) {
    const label = reportError(error, 'Failed to place order');

    if (error instanceof NutsRedirect) {
      router.push(error.url);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    paymentError.value = label;

    if (!paymentErrorIntersection.value) {
      paymentErrorElement.value?.scrollIntoView({
        behavior: 'smooth',
      });
    }

    CheckoutEvents.event({ action: 'Payment', label, category: 'Checkout Error' });

    submitting.value = false;
  }

  braintreeStore.cleanCache();
};

const disablePlacerOrderButton = computed(
  () =>
    !(hasValidPaymentMethod.value && !(unassignedLineItems.value.length > 0)) ||
    placeOrderDisabledByAutoDelivery.value ||
    (displayConfirmCVV.value && !confirmationCvv.value) ||
    props.disablePlaceOrderButton,
);

watch(selectedPaymentOption, (method) => {
  if (method !== 'Offline') {
    orderTags.value = [];
  }

  CheckoutEvents.option(4, `${method}`);
  CheckoutEvents.event({ action: 'Payment', label: `Payment Method: ${method}` });
});

const paypalLogo = nutshell['img/paypal-logo.svg'];
</script>

<template>
  <div :class="{ 'opacity-50 pointer-events-none': submitting }">
    <WindowObserver
      :options="{ rootMargin: '20px', threshold: 1 }"
      @intersect="intersectPaymentError"
      @exit-view="exitPaymentError"
    >
      <div ref="paymentErrorElement">
        <Notification v-if="paymentError" theme="error">
          <div>{{ paymentError }}</div>
        </Notification>
      </div>
    </WindowObserver>
    <div :class="{ 'mt-3 md:mt-6': paymentError }">
      <InformationBanner
        v-if="containsAutoDelivery && hasOnlyUSBankAccount"
        variant="warning"
        class="mb-4"
      >
        <BaseBodyText class="font-semibold">
          Your order contains Auto-Delivery items. Unfortunately, we do not support checking account
          payments for Auto-Delivery.
        </BaseBodyText>
        <BaseBodyText>
          Please select or add a different payment method, or place a separate order for
          Auto-Delivery items.
        </BaseBodyText>
      </InformationBanner>

      <transition name="fade" mode="out-in">
        <SavedCreditCards
          v-if="showSavedCards"
          v-model="paymentOptions.CreditCard"
          :braintreeToken
          :containsAutoDelivery
          :saveCardOption
          :savedCards="savedCreditCards"
          @cancel="showSavedCards = false"
          @saved="showSavedCards = false"
        />
        <div v-else>
          <div
            aria-label="Payment Method Options"
            data-test="payment-method-options"
            role="radiogroup"
          >
            <RadioGroup
              v-model="selectedPaymentOption"
              :data-test="(method) => `payment-method-selector-${method.type}`"
              name="payment-method-type"
              :items="otherPaymentMethods ?? []"
              :keyGetter="(paymentMethod) => paymentMethod.type"
              :valueGetter="(paymentMethod) => paymentMethod.type"
            >
              <template #top-options>
                <RadioGroupItem
                  v-model="selectedPaymentOption"
                  :class="{
                    'border-neutral-300 bg-white': selectedPaymentOption !== 'CreditCard',
                  }"
                  data-test="payment-method-selector-credit-card"
                  name="payment-method-type"
                  position="first"
                  value="CreditCard"
                >
                  <div
                    class="flex items-center justify-between w-full leading-6 cursor-pointer md:leading-7"
                    :class="
                      selectedPaymentOption === 'CreditCard' ? 'font-semibold' : 'font-normal'
                    "
                  >
                    <template v-if="savedCreditCards.length && paymentOptions.CreditCard">
                      <span>
                        {{ paymentOptions.CreditCard.description }}
                        <CreditCardLogo class="w-8 ml-1" :cardType="currentCardType" />
                      </span>
                      <TextButton
                        class="justify-self-end"
                        data-test="change-payment-method"
                        @click="showSavedCards = true"
                        @keydown.enter.prevent="showSavedCards = true"
                        @keydown.space.prevent="showSavedCards = true"
                      >
                        Change Method
                      </TextButton>
                    </template>
                    <template v-else>Credit/Debit Card</template>
                  </div>
                  <template #bottom>
                    <ConfirmCvv
                      v-if="displayConfirmCVV"
                      v-model="confirmationCvv"
                      :braintreeToken
                      class="my-4"
                      data-test="checkout-payment-confirm-cvv"
                      :cardType="currentCardType"
                      :key="paymentOptions.CreditCard?.details?.lastFour"
                    />
                    <transition name="fade">
                      <template v-if="!savedCreditCards.length">
                        <CreditCardForm
                          v-show="showNewCardForm"
                          v-model="paymentOptions.CreditCard"
                          :braintreeToken
                          class="mt-3 cursor-default"
                          data-test="new-card-form"
                          :saveCardOption
                          :stepNumber="4"
                        />
                      </template>
                    </transition>
                  </template>
                </RadioGroupItem>
              </template>
              <template #default="{ item: paymentMethod }">
                <div
                  class="w-full leading-6 cursor-pointer md:leading-7"
                  :class="
                    selectedPaymentOption === paymentMethod.type ? 'font-semibold' : 'font-normal'
                  "
                >
                  <template v-if="paymentMethod?.type === 'PayPal'">
                    <ThirdPartyPaymentEmailDisplay
                      v-if="savedPaypal"
                      :email="savedPaypal.external_account_token ?? ''"
                      provider="PayPal"
                    />
                    <template v-else>
                      {{ paymentMethod.label }}
                    </template>
                    <img :src="paypalLogo" alt="PayPal" class="w-20 ml-1" />
                  </template>
                  <template v-else-if="paymentMethod.type === 'ApplePay'">
                    <ThirdPartyPaymentEmailDisplay
                      v-if="savedApplePay"
                      :email="savedApplePay.external_account_token ?? ''"
                      provider="Apple Pay"
                    />
                    <template v-else>
                      {{ paymentMethod.label }}
                    </template>
                    <img src="@/assets/apple-pay-mark.svg" alt="Apple Pay" class="ml-1 w-11" />
                  </template>
                  <template v-else>
                    {{ paymentMethod.label }}
                  </template>
                </div>
              </template>
              <template v-if="selectedPaymentOption === 'Offline'" #bottom-options />
            </RadioGroup>
            <div
              v-if="cxMode"
              v-show="selectedPaymentOption === 'Offline'"
              class="px-4 py-3 border-b border-solid border-x border-neutral-300 bg-neutral-100"
            >
              <OrderTags v-model="orderTags" />
              <div v-if="cartCustomFields?.reshippedFromOrderNumber">
                This order is a reshipment of order
                {{ cartCustomFields.reshippedFromOrderNumber }}.
              </div>
              <BillingAddress v-model="paymentOptions.Offline" />
            </div>
          </div>

          <FormControlsWrapper
            v-if="showPriceAdjustment"
            class="mt-5"
            :selected="usePriceAdjustment"
          >
            <PriceAdjustment v-model="usePriceAdjustment" />
          </FormControlsWrapper>

          <FormControlsWrapper
            v-if="giftCertificatePaymentMethod"
            class="mt-5"
            :selected="useGiftCertificate"
          >
            <GiftCertificateOption
              v-model="useGiftCertificate"
              :estimatedGiftCertificateCharge
              :giftCertificate
              :stepNumber="4"
              @set-gift-certificate="setGiftCertificate"
            />
          </FormControlsWrapper>
        </div>
      </transition>
    </div>

    <DiscountCodeEntry v-if="cxMode" class="mt-6 md:mt-9" />

    <div v-if="showPurchaseOrderNumber" class="mt-2 md:mt-4">
      <strong>Purchase Order Number</strong>
      <FormInput
        v-model="purchaseOrderNumber"
        class="mt-2"
        :inputAttributes="{
          placeholder: 'Purchase Order Number',
          name: 'setPONumber',
        }"
      />
    </div>

    <template v-if="!customer && customerEmail">
      <CxCreateAccount
        v-if="cxMode"
        v-model:cxCreateAccount="cxCreateAccount"
        v-model:isB2b="isB2B"
        class="mt-6 md:mt-9"
      />
      <CreateAccount
        v-else
        v-model:password="createAccountPassword"
        v-model:isB2b="isB2B"
        class="mt-6 md:mt-9"
      />
    </template>

    <CxNewsletterSignup
      v-if="cxMode && customerEmail && offerNewsletter"
      v-model="newsletterSignup"
      class="mt-4"
    />

    <div class="mt-8 md:mt-12">
      <CheckoutApplePayButton
        v-if="selectedPaymentOption === 'ApplePay'"
        :containsAutoDelivery
        :createAccountPassword
        :disabled="submitting || placeOrderDisabledByAutoDelivery || disablePlaceOrderButton"
        :isB2b="isB2B"
        :savedPaymentMethods
      />
      <BraintreePaypalButton
        v-else-if="selectedPaymentOption === 'PayPal'"
        v-model="paymentOptions.PayPal"
        :amount="estimatedAmountToCharge"
        :autoSetDataUserIdToken="!!savedPaypal"
        :disabled="submitting || placeOrderDisabledByAutoDelivery || disablePlaceOrderButton"
        @submit="onPlaceOrder"
      />

      <ThemedButton
        v-else
        data-test="place-order-btn"
        :disabled="disablePlacerOrderButton"
        :isLoading="submitting"
        fullWidth
        theme="green"
        @click="onPlaceOrder"
      >
        {{ containsAutoDelivery && !customer ? 'Create Account & Place Order' : 'Place Order' }}
      </ThemedButton>
      <p v-if="!saveCardOption" class="mt-5 mb-0 text-xs leading-4">
        By placing your order, you agree to the Auto-Delivery terms and conditions for the
        subscription and authorize us to charge your default payment method or another payment
        method on file. Your subscription continues until you cancel it. You can cancel at any time
        via the Cancel subscription button on the Auto-Delivery page.
      </p>
      <p class="mt-2 mb-0 text-xs leading-4 md:mt-5">
        By placing your order, you agree to our
        <a href="/privacy" target="_blank" class="underline color-inherit"> privacy policy </a
        >,<br />
        <a href="/terms-and-conditions" target="_blank" class="underline color-inherit">
          terms &amp; conditions
        </a>
        and
        <a href="/california-privacy-notice" target="_blank" class="underline color-inherit">
          CA privacy notice </a
        >.
      </p>
    </div>
  </div>
</template>
